/**
 * Persistent auto-naming for layout editor nodes.
 *
 * - `scene.nodeNameCounters` stores the last allocated index per type key.
 * - Each node stores a stable `nameIndex` (decoupled from `label`).
 * - Display: custom `label` if set, otherwise `"{Type} {nameIndex}"`.
 */

import type { ContainerNode, LayoutDocument, LayoutNode } from "./types.js";

/**
 * Collects row templates attached to a layoutList component node.
 *
 * @param node - Layout node to inspect.
 * @returns Row template containers (primary + variants), or an empty array.
 */
export function getAllLayoutListRowTemplates(
  node: LayoutNode,
): ContainerNode[] {
  if (node.kind !== "component" || node.component !== "layoutList") return [];
  const props = (node.props ?? {}) as Partial<{
    rowTemplate?: ContainerNode;
    rowVariants?: Array<{ template: ContainerNode }>;
  }>;
  const templates: ContainerNode[] = [];
  if (props.rowTemplate) templates.push(props.rowTemplate);
  for (const variant of props.rowVariants ?? []) {
    templates.push(variant.template);
  }
  return templates;
}

/**
 * Stable key used to group nodes for auto-numbering counters.
 *
 * @param node - Layout node to classify.
 * @returns Type key (e.g. `primitive:text`, `primitive:shape:ellipse`).
 */
export function getLayoutNodeTypeKey(node: LayoutNode): string {
  if (node.kind === "primitive") {
    if (node.primitive === "shape") {
      return `primitive:shape:${node.shapeType ?? "rectangle"}`;
    }
    return `primitive:${node.primitive}`;
  }
  if (node.kind === "component") {
    return `component:${node.component}`;
  }
  return "container";
}

export type LayoutNodeTypeLabelResolver = (node: LayoutNode) => string;

/**
 * Resolves the visible editor name for a node.
 *
 * @param node - Node to display.
 * @param getTypeLabel - Localized type name resolver.
 * @returns Custom label when set, otherwise `"{Type} {nameIndex}"`.
 */
export function resolveNodeDisplayName(
  node: LayoutNode,
  getTypeLabel: LayoutNodeTypeLabelResolver,
): string {
  const customLabel = node.label?.trim();
  if (customLabel) return customLabel;
  const typeLabel = getTypeLabel(node);
  if (typeof node.nameIndex === "number" && node.nameIndex > 0) {
    return `${typeLabel} ${node.nameIndex}`;
  }
  return typeLabel;
}

/**
 * Allocates a fresh `nameIndex` for a node tree (root + nested).
 *
 * Existing custom `label` values are preserved. Used for add / duplicate / paste.
 *
 * @param node - Root node to index.
 * @param counters - Current scene counters.
 * @returns Node tree with `nameIndex` set and updated counters.
 */
export function allocateNamesForTree(
  node: LayoutNode,
  counters: Record<string, number>,
): { node: LayoutNode; counters: Record<string, number> } {
  let nextCounters = { ...counters };
  const key = getLayoutNodeTypeKey(node);
  const index = (nextCounters[key] ?? 0) + 1;
  nextCounters[key] = index;
  let result: LayoutNode = {
    ...node,
    nameIndex: index,
  };

  if (result.kind === "container") {
    const children: LayoutNode[] = [];
    for (const child of result.children) {
      const allocated = allocateNamesForTree(child, nextCounters);
      nextCounters = allocated.counters;
      children.push(allocated.node);
    }
    result = { ...result, children };
  }

  if (result.kind === "component" && result.component === "layoutList") {
    const props = { ...(result.props ?? {}) } as Partial<{
      rowTemplate?: ContainerNode;
      rowVariants?: Array<{ name: string; template: ContainerNode }>;
    }>;
    if (props.rowTemplate) {
      const allocated = allocateNamesForTree(props.rowTemplate, nextCounters);
      nextCounters = allocated.counters;
      props.rowTemplate = allocated.node as ContainerNode;
    }
    if (props.rowVariants?.length) {
      props.rowVariants = props.rowVariants.map((variant) => {
        const allocated = allocateNamesForTree(variant.template, nextCounters);
        nextCounters = allocated.counters;
        return {
          ...variant,
          template: allocated.node as ContainerNode,
        };
      });
    }
    result = { ...result, props };
  }

  return { node: result, counters: nextCounters };
}

/**
 * Allocates a single new `nameIndex` for a node without touching its children.
 *
 * @param node - Node to index.
 * @param counters - Current scene counters.
 * @returns Named node and updated counters.
 */
export function allocateNameForNode(
  node: LayoutNode,
  counters: Record<string, number>,
): { node: LayoutNode; counters: Record<string, number> } {
  const key = getLayoutNodeTypeKey(node);
  const index = (counters[key] ?? 0) + 1;
  return {
    node: { ...node, nameIndex: index },
    counters: { ...counters, [key]: index },
  };
}

/**
 * Ensures every node has a persistent `nameIndex` and that
 * `scene.nodeNameCounters` is high enough for future allocations.
 *
 * Does not write `label`. Idempotent when all nodes already have `nameIndex`.
 *
 * @param document - Layout document to migrate.
 * @returns Cloned document with `nameIndex` / counters applied.
 */
export function ensureLayoutNodeNames(
  document: LayoutDocument,
): LayoutDocument {
  const doc = structuredClone(document);
  let counters: Record<string, number> = {
    ...(doc.scene.nodeNameCounters ?? {}),
  };

  const visit = (nodes: LayoutNode[]): void => {
    for (const node of nodes) {
      const key = getLayoutNodeTypeKey(node);
      if (typeof node.nameIndex === "number" && node.nameIndex > 0) {
        counters[key] = Math.max(counters[key] ?? 0, node.nameIndex);
      } else {
        const index = (counters[key] ?? 0) + 1;
        counters[key] = index;
        node.nameIndex = index;
      }

      for (const template of getAllLayoutListRowTemplates(node)) {
        visit([template]);
      }
      if (node.kind === "container") {
        visit(node.children);
      }
    }
  };

  visit(doc.scene.children);
  doc.scene.nodeNameCounters = counters;
  return doc;
}
